import React, { useState, useEffect } from 'react';
import { Bell, X } from 'lucide-react';
import { usePushNotifications } from '../hooks/usePushNotifications';

/**
 * Componente para solicitar permissão de push notifications
 * Aparece como um banner flutuante no mobile após o usuário fazer login
 */
const PushNotificationPrompt = () => {
  const [show, setShow] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const { permission, isSupported, isSubscribed, loading, subscribe } = usePushNotifications();

  useEffect(() => {
    // Verificar se deve mostrar o prompt
    const shouldShow = () => {
      // Não mostrar se já inscrito
      if (isSubscribed) return false;
      
      // Não mostrar se permissão negada
      if (permission === 'denied') return false;
      
      // Não mostrar se já foi dispensado nesta sessão
      if (dismissed) return false;
      
      // Não mostrar se usuário já dispensou permanentemente
      const permanentlyDismissed = localStorage.getItem('push_prompt_dismissed');
      if (permanentlyDismissed) return false;
      
      // Verificar se está logado
      const token = localStorage.getItem('token');
      if (!token) return false;
      
      return isSupported;
    };

    // Mostrar após 3 segundos de interação
    const timer = setTimeout(() => {
      if (shouldShow()) {
        setShow(true);
      }
    }, 3000);

    return () => clearTimeout(timer);
  }, [isSupported, isSubscribed, permission, dismissed]);

  const handleEnable = async () => {
    const success = await subscribe();
    if (success) {
      setShow(false);
    }
  };

  const handleDismiss = () => {
    setDismissed(true);
    setShow(false);
  };

  const handleNeverAsk = () => {
    localStorage.setItem('push_prompt_dismissed', 'true');
    setDismissed(true);
    setShow(false);
  };

  if (!show) return null;

  return (
    <div className="fixed bottom-24 left-4 right-4 md:bottom-8 md:left-auto md:right-8 md:max-w-sm z-50 animate-fade-in-up">
      <div className="bg-gradient-to-br from-[#1A1A1A] to-[#2A1A25] border border-[#C79F5E]/30 rounded-2xl p-4 shadow-xl shadow-black/50">
        {/* Header */}
        <div className="flex items-start gap-3">
          <div className="w-12 h-12 bg-gradient-to-br from-[#C79F5E] to-[#A67C3D] rounded-xl flex items-center justify-center flex-shrink-0">
            <Bell className="w-6 h-6 text-black" />
          </div>
          <div className="flex-1">
            <h3 className="text-white font-bold text-base">Ativar Notificações</h3>
            <p className="text-gray-400 text-sm mt-1">
              Receba alertas de novos assinantes, pagamentos e mensagens instantaneamente
            </p>
          </div>
          <button
            onClick={handleDismiss}
            className="text-gray-500 hover:text-gray-300 p-1"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Buttons */}
        <div className="flex gap-3 mt-4">
          <button
            onClick={handleNeverAsk}
            className="flex-1 py-2.5 px-4 text-gray-400 text-sm font-medium hover:text-gray-200 transition-colors"
          >
            Agora não
          </button>
          <button
            onClick={handleEnable}
            disabled={loading}
            className="flex-1 bg-gradient-to-r from-[#C79F5E] to-[#A67C3D] text-black py-2.5 px-4 rounded-xl font-bold text-sm hover:shadow-lg hover:shadow-[#C79F5E]/30 transition-all disabled:opacity-50"
          >
            {loading ? 'Ativando...' : 'Ativar'}
          </button>
        </div>

        {/* Benefits */}
        <div className="mt-4 pt-3 border-t border-white/10">
          <div className="flex items-center gap-2 text-xs text-gray-500">
            <span className="w-1.5 h-1.5 bg-green-500 rounded-full"></span>
            <span>Novos assinantes</span>
            <span className="w-1.5 h-1.5 bg-green-500 rounded-full ml-2"></span>
            <span>Pagamentos</span>
            <span className="w-1.5 h-1.5 bg-green-500 rounded-full ml-2"></span>
            <span>Mensagens</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PushNotificationPrompt;
